<template>
  <div
    class="custom-single-select"
    :class="{ 'is-open': isOpen, 'has-selection': selectedValue }"
  >
    <div class="select-trigger" @click="toggleDropdown" :class="{ 'is-focused': isOpen }">
      <div class="selected-value-display">
        <span v-if="hasSelection" class="selected-text">{{ selectedText }}</span>
        <span v-else class="placeholder">{{ placeholder }}</span>
      </div>
      <div class="select-arrow">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>

    <div v-if="isOpen" class="custom-dropdown-menu">
      <div class="dropdown-header">
        <div class="search-container">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search banks..."
            @input="filterOptions"
            @click.stop
          />
        </div>
      </div>

      <div class="options-list">
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="option-item"
          :class="{ 'is-selected': option.value === selectedValue }"
          @click.stop="selectOption(option)"
        >
          <span class="option-text">{{ option.text }}</span>
        </div>
      </div>

      <div v-if="filteredOptions.length === 0" class="no-options">No banks found</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomSingleSelect',
  props: {
    value: {
      type: [String, Number],
      default: null,
    },
    modelValue: {
      type: [String, Number],
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Select an option',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  emits: ['input', 'change'],
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      filteredOptions: [],
      internalValue: null,
    };
  },
  computed: {
    selectedValue() {
      if (this.internalValue !== null && this.internalValue !== undefined) return this.internalValue;
      return this.value !== null && this.value !== undefined ? this.value : this.modelValue;
    },
    hasSelection() {
      const v = this.selectedValue;
      return v !== null && v !== undefined && String(v).trim() !== '';
    },
    selectedText() {
      const option = this.options.find((opt) => opt.value === this.selectedValue);
      return option ? option.text : this.selectedValue;
    },
  },
  watch: {
    options: {
      immediate: true,
      handler() {
        this.filteredOptions = [...this.options];
      },
    },
    value: {
      immediate: true,
      handler(newValue) {
        this.internalValue = newValue;
      },
    },
    modelValue: {
      immediate: true,
      handler(newValue) {
        this.internalValue = newValue;
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.searchQuery = '';
        this.filteredOptions = [...this.options];
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },
    selectOption(option) {
      this.internalValue = option.value;
      this.$emit('input', option.value);
      this.$emit('update:modelValue', option.value);
      this.$emit('change', option.value);
      this.isOpen = false;
    },
    filterOptions() {
      if (!this.searchQuery.trim()) {
        this.filteredOptions = [...this.options];
      } else {
        this.filteredOptions = this.options.filter((option) =>
          option.text.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
label {
  margin-bottom: 0 !important;
}

.custom-single-select {
  position: relative;
  width: 100%;
  font-family: 'Inter', sans-serif;

  .select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 32px;
    padding: 6px 12px;
    background-color: #ffffff;
    border: 1px solid #303030;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &.is-focused {
      border-color: #00315a;
      box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
    }

    .selected-value-display {
      flex: 1;
      min-width: 0;

      .selected-text {
        color: #24292f;
        font-size: 14px;
      }

      .placeholder {
        color: #656d76;
        font-size: 14px;
      }
    }

    .select-arrow {
      color: #656d76;
      margin-left: 8px;
      transition: transform 0.2s ease;

      svg {
        width: 12px;
        height: 12px;
      }
    }
  }

  &.is-open .select-arrow {
    transform: rotate(180deg);
  }

  .custom-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    margin-top: 4px;
    background-color: #ffffff;
    border: 1px solid #303030;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(140, 149, 159, 0.2);
    max-height: 300px;
    overflow: hidden;

    .dropdown-header {
      padding: 12px;
      border-bottom: 1px solid #303030;

      .search-container {
        .search-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #303030;
          border-radius: 6px;
          font-size: 13px;
          outline: none;

          &:focus {
            border-color: #00315a;
            box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.1);
          }

          &::placeholder {
            color: #656d76;
          }
        }
      }
    }

    .options-list {
      max-height: 220px;
      overflow-y: auto;

      .option-item {
        padding: 10px 12px;
        cursor: pointer;
        transition: background-color 0.15s ease;

        &:hover {
          background-color: #f6f8fa;
        }

        &.is-selected {
          background-color: #ddf4ff;
          color: #00315a;
          font-weight: 500;
        }

        .option-text {
          font-size: 14px;
          color: #24292f;
        }
      }
    }

    .no-options {
      padding: 16px;
      text-align: center;
      color: #656d76;
      font-size: 14px;
    }
  }

  &.has-selection .select-trigger {
    border-color: #00315a;
  }
}

@media (max-width: 768px) {
  .custom-single-select {
    .select-trigger {
      .selected-value-display {
        .selected-text,
        .placeholder {
          font-size: 13px;
        }
      }
    }
  }
}
</style>
